import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { OdooAttemptResult } from "./SelectedProducts.types";
import ComprasService, {
  type OdooPoDetails,
} from "../../services/comprasService";

export interface SelectedProductsOdooResponsesProps {
  attempts: OdooAttemptResult[];
  createdPoIds: Array<number | string>;
  grandTotal: number;
  baseMargin: number;
  onRetry?: () => void;
  retryDisabled?: boolean;
  errorMessage?: string | null;
}

const fmtCurrency = (value: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.max(0, value));

export const SelectedProductsOdooResponses: React.FC<
  SelectedProductsOdooResponsesProps
> = ({ attempts, createdPoIds, onRetry, retryDisabled, errorMessage }) => {
  const successCount = React.useMemo(
    () => attempts.filter((a) => a.status === "success").length,
    [attempts],
  );
  const hasErrors = successCount === 0 || Boolean(errorMessage);

  const [odooDetails, setOdooDetails] = React.useState<OdooPoDetails[] | null>(
    null,
  );
  const [loadingOdoo, setLoadingOdoo] = React.useState(false);
  const [odooError, setOdooError] = React.useState<string | null>(null);

  // Cargar datos reales desde Odoo cuando hay POs creadas exitosamente
  React.useEffect(() => {
    if (createdPoIds.length > 0 && !hasErrors) {
      const fetchOdooDetails = async () => {
        setLoadingOdoo(true);
        setOdooError(null);
        try {
          console.log("🔄 Cargando detalles reales desde Odoo...");
          const response = await ComprasService.getOdooPoDetails(createdPoIds);
          setOdooDetails(response.data);
          console.log("✅ Detalles cargados desde Odoo:", response.data);
        } catch (error: any) {
          console.error("❌ Error cargando detalles desde Odoo:", error);
          setOdooError(error?.message || "Error desconocido");
        } finally {
          setLoadingOdoo(false);
        }
      };
      void fetchOdooDetails();
    }
  }, [createdPoIds, hasErrors]);

  const handleOpenOdoo = React.useCallback((poId: number) => {
    // Abrir en nueva pestaña la PO en Odoo
    const odooUrl = `https://masporpieza.mx/odoo/purchase/${poId}`;
    window.open(odooUrl, "_blank");
  }, []);

  // Si hay errores, mostrar vista de error
  if (hasErrors) {
    return (
      <Stack spacing={3} sx={{ mt: 2, pb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ErrorIcon sx={{ fontSize: 48, color: "error.main" }} />
          <Stack>
            <Typography variant="h5" fontWeight={700} color="error.main">
              No se pudo completar la operación
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ocurrió un problema al intentar crear las órdenes de compra en
              Odoo
            </Typography>
          </Stack>
        </Stack>

        {errorMessage && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="body1" fontWeight={600}>
              Detalles del error:
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {errorMessage}
            </Typography>
          </Alert>
        )}

        {attempts.length > 0 && (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Intentos realizados
            </Typography>
            {attempts.map((attempt) => (
              <Paper
                key={attempt.attemptId}
                elevation={1}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  borderLeft: 4,
                  borderColor: "error.main",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="h6" fontWeight={600}>
                      {attempt.supplierName ||
                        `Proveedor ${attempt.supplierId || "Desconocido"}`}
                    </Typography>
                    <Chip
                      icon={<ErrorIcon />}
                      label="Error"
                      size="medium"
                      color="error"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                  {attempt.errorMessage && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        {attempt.errorMessage}
                      </Typography>
                    </Alert>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Fecha:{" "}
                    {new Date(attempt.createdAt).toLocaleString("es-MX", {
                      dateStyle: "long",
                      timeStyle: "short",
                    })}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {onRetry && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ pt: 2 }}
          >
            <Button
              size="large"
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              disabled={Boolean(retryDisabled)}
              sx={{ minWidth: 250 }}
            >
              Reintentar Creación
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  // Vista de éxito
  return (
    <Stack spacing={4} sx={{ mt: 2, pb: 4 }}>
      {/* Header de éxito */}
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} />
          <Stack>
            <Typography variant="h5" fontWeight={700} color="success.main">
              ¡Órdenes de Compra Creadas Exitosamente!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Se crearon {successCount}{" "}
              {successCount === 1 ? "orden" : "órdenes"} de compra en Odoo
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Detalle de cada orden */}
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Detalle de las Órdenes
        </Typography>

        {/* Loading State */}
        {loadingOdoo && (
          <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={40} />
              <Typography variant="body1" color="text.secondary">
                Cargando información desde Odoo...
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Error loading from Odoo */}
        {odooError && !loadingOdoo && (
          <Alert severity="warning">
            <Typography variant="body2">
              No se pudo cargar la información actualizada desde Odoo:{" "}
              {odooError}
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              Mostrando información básica de las órdenes creadas.
            </Typography>
          </Alert>
        )}

        {/* Success: Show real data from Odoo */}
        {!loadingOdoo && odooDetails && odooDetails.length > 0
          ? odooDetails.map((po) => (
              <Card
                key={po.id}
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    {/* Header de la orden */}
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="h6" fontWeight={700}>
                            {po.partnerName}
                          </Typography>
                          <Chip
                            label={po.name}
                            size="small"
                            color="success"
                            onClick={() => handleOpenOdoo(po.id)}
                            icon={<OpenInNewIcon fontSize="small" />}
                            sx={{
                              fontWeight: 700,
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "success.dark" },
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Proveedor ID: {po.partnerId}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={
                            po.state === "draft"
                              ? "Borrador"
                              : po.state === "sent"
                                ? "Enviada"
                                : po.state === "purchase"
                                  ? "Confirmada"
                                  : po.state
                          }
                          size="small"
                          color={po.state === "draft" ? "warning" : "success"}
                          sx={{ textTransform: "capitalize" }}
                        />
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </Stack>
                    </Stack>

                    {/* Información de la orden */}
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={4} flexWrap="wrap">
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Productos
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {po.lineCount}{" "}
                            {po.lineCount === 1 ? "artículo" : "artículos"}
                          </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Fecha de creación
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {new Date(po.createDate).toLocaleString("es-MX", {
                              dateStyle: "medium",
                              timeStyle: "short",
                            })}
                          </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Moneda
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {po.currencyName}
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Tabla de productos */}
                      {po.lines.length > 0 && (
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{ border: "1px solid", borderColor: "divider" }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow
                                sx={{ backgroundColor: "action.hover" }}
                              >
                                <TableCell>
                                  <strong>Producto</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>Cantidad</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>Precio Unit.</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>Total</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {po.lines.map((line) => (
                                <TableRow key={line.id} hover>
                                  <TableCell>
                                    <Stack spacing={0.25}>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                      >
                                        {line.productName}
                                      </Typography>
                                      {line.description &&
                                        line.description !==
                                          line.productName && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {line.description}
                                          </Typography>
                                        )}
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="right">
                                    {line.quantity.toFixed(0)} {line.uom}
                                  </TableCell>
                                  <TableCell align="right">
                                    {fmtCurrency(line.priceUnit)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {fmtCurrency(line.priceSubtotal)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      {/* Totales desde Odoo */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Subtotal:
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {fmtCurrency(po.amountUntaxed)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              IVA:
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {fmtCurrency(po.amountTax)}
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              borderTop: "2px solid",
                              borderColor: "divider",
                              my: 1,
                            }}
                          />
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h6" fontWeight={700}>
                              Total:
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color="primary.main"
                            >
                              {fmtCurrency(po.amountTotal)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          : !loadingOdoo &&
              !odooError &&
              attempts.filter((attempt) => attempt.status === "success")
                .length > 0
            ? // Fallback: mostrar info básica si no hay detalles de Odoo
              attempts
                .filter((attempt) => attempt.status === "success")
                .map((attempt) => (
                  <Card
                    key={attempt.attemptId}
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="h6" fontWeight={700}>
                            {attempt.supplierName ||
                              `Proveedor ${attempt.supplierId || "Desconocido"}`}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            {attempt.odooPoId && (
                              <Chip
                                label={`OC-${attempt.odooPoId}`}
                                size="small"
                                color="success"
                                onClick={() =>
                                  handleOpenOdoo(attempt.odooPoId!)
                                }
                                sx={{ fontWeight: 700, cursor: "pointer" }}
                              />
                            )}
                            <CheckCircleIcon sx={{ color: "success.main" }} />
                          </Stack>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {attempt.lineCount}{" "}
                          {attempt.lineCount === 1 ? "artículo" : "artículos"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Creada:{" "}
                          {new Date(attempt.createdAt).toLocaleString("es-MX", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
            : null}
      </Stack>
    </Stack>
  );
};

export default SelectedProductsOdooResponses;
